/**
 * ExternalResourcesPage — Vincent B592 (Eric 2026-07-28 SPEC STRICK)
 *
 * Nouvelle section « Ressources externes » sous Connaissances › Quiz.
 * Deux modes selon `canEdit` (retourné par GET /api/external-resources) :
 *
 *   - Mode APPRENANT (canEdit=false) : liste hiérarchique en lecture seule,
 *     chaque item est un lien cliquable (target="_blank" + rel).
 *
 *   - Mode CONCEPTEUR (canEdit=true) : add/edit/remove catégories & items,
 *     déplacement dans l'arbre, bouton réindexation par item.
 *
 * Note : les URLs sont indexées côté backend par le chatbot RAG (chunks
 * dans `external_resource_chunks`) mais JAMAIS lues par le quiz generator
 * (flag `use_for_quiz=False`).
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import {
  Folder, FolderOpen, Link as LinkIcon, Plus, PencilSimple,
  Trash, ArrowClockwise, CheckCircle, WarningCircle, ArrowSquareOut, Eye,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { useOneTimeHint } from "../hooks/useOneTimeHint";
import { useLang } from "../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

function buildTree(resources) {
  const byId = Object.fromEntries(resources.map((r) => [r.id, { ...r, children: [] }]));
  const roots = [];
  for (const r of Object.values(byId)) {
    if (r.parent_id && byId[r.parent_id]) {
      byId[r.parent_id].children.push(r);
    } else {
      roots.push(r);
    }
  }
  const sortRec = (nodes) => {
    nodes.sort((a, b) => (a.order || 0) - (b.order || 0) || (a.title || "").localeCompare(b.title || "", "fr"));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

export default function ExternalResourcesPage() {
  const { tr } = useLang();
  const { user } = useAuth();
  // Vincent B592.d (Eric 2026-07-28 SPEC STRICK) — Badge visible UNIQUEMENT
  // pour founder / admin. Les concepteurs peuvent réindexer mais n'ont pas
  // besoin de voir la santé du cron (ceci pour éviter la confusion).
  const isFounderOrAdmin = Boolean(
    user && (
      user.is_founder ||
      (user.role || "").toLowerCase() === "admin" ||
      (user.role || "").toLowerCase() === "founder" ||
      (user.platform_role || "").toLowerCase() === "root_owner"
    )
  );
  const [resources, setResources] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null); // {mode: "create-cat"|"create-item"|"edit", parent_id?, resource?}
  const [expanded, setExpanded] = useState(new Set());
  // Vincent B592.a — Drag-drop state
  const [dragOverId, setDragOverId] = useState(null);
  // Vincent B592.b — Preview modal state
  const [previewResourceId, setPreviewResourceId] = useState(null);
  // Vincent B592.d — Badge « Dernière réindexation » (founder/admin only)
  const [reindexStatus, setReindexStatus] = useState(null);
  // Vincent B598.c (Eric 2026-07-29) — Hint 1× par session sur le bouton
  // « Réindexer maintenant » pour signaler que le cron auto s'occupe déjà
  // du rafraîchissement hebdomadaire (évite les clicks inutiles).
  const { show: showReindexHint, dismiss: dismissReindexHint } = useOneTimeHint(
    "kn-ext-reindex-hint-seen", 10000, canEdit
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/external-resources`, { withCredentials: true });
      setResources(data.resources || []);
      setCanEdit(!!data.can_edit);
    } catch (e) {
      toast.error(tr("Impossible de charger les ressources externes", "Could not load the external resources"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Vincent B592.d (Eric 2026-07-28) — Fetch reindex status (founder/admin only)
  useEffect(() => {
    if (!isFounderOrAdmin) return;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/api/external-resources/reindex-status`, { withCredentials: true });
        if (!cancelled) setReindexStatus(data);
      } catch (_) { /* silent — pas critique */ }
    };
    fetchStatus();
    return () => { cancelled = true; };
  }, [isFounderOrAdmin]);

  const tree = buildTree(resources);

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const deleteResource = async (r) => {
    const ok = await confirmDialog(r.is_category
      ? `Supprimer la catégorie « ${r.title} » et TOUS ses sous-éléments ?`
      : `Supprimer « ${r.title} » ?`
    );
    if (!ok) return;
    try {
      const { data } = await axios.delete(`${API}/api/external-resources/${r.id}`, { withCredentials: true });
      toast.success(`Supprimé (${data.deleted_resources} élément(s), ${data.deleted_chunks} chunk(s) chatbot)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const reindex = async (r) => {
    try {
      await axios.post(`${API}/api/external-resources/${r.id}/reindex`, {}, { withCredentials: true });
      toast.success(tr("Réindexation lancée en arrière-plan", "Reindexing started in the background"));
      setTimeout(load, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Réindexation impossible", "Reindexing failed"));
    }
  };

  // Vincent B592.c (Eric 2026-07-28) — Bouton admin « Réindexer toutes les ressources »
  const [reindexAllBusy, setReindexAllBusy] = useState(false);
  const reindexAll = async () => {
    const ok = await confirmDialog(tr("Réindexer TOUTES les ressources externes maintenant ? Cela peut prendre plusieurs minutes selon le nombre de liens.", "Reindex EVERY external resource now? This can take several minutes depending on the number of links."));
    if (!ok) return;
    setReindexAllBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/external-resources/reindex-all`, {}, { withCredentials: true });
      if (data.status === "noop") {
        toast.info(data.message || tr("Aucune ressource à réindexer.", "No resource to reindex."));
      } else {
        toast.success(data.message || tr("Réindexation lancée en arrière-plan.", "Reindexing started in the background."));
      }
      setTimeout(load, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Réindexation globale impossible", "Global reindexing failed"));
    } finally {
      setReindexAllBusy(false);
    }
  };

  // Vincent B592.a (Eric 2026-07-28) — Drag-drop d'un item ou sous-catégorie
  // vers une catégorie cible. Utilise HTML5 native drag-drop (aucune lib).
  const onDropOnCategory = async (draggedId, targetCatId) => {
    setDragOverId(null);
    if (!draggedId || draggedId === targetCatId) return;
    const current = resources.find((r) => r.id === draggedId);
    if (!current) return;
    if (current.parent_id === targetCatId) return;  // no-op
    try {
      await axios.patch(
        `${API}/api/external-resources/${draggedId}`,
        { parent_id: targetCatId },
        { withCredentials: true }
      );
      toast.success(tr("Déplacé", "Moved"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Déplacement impossible", "Move failed"));
    }
  };
  // Drop sur la racine (retire le parent_id)
  const onDropOnRoot = async (draggedId) => {
    setDragOverId(null);
    if (!draggedId) return;
    const current = resources.find((r) => r.id === draggedId);
    if (!current || !current.parent_id) return;
    try {
      await axios.patch(
        `${API}/api/external-resources/${draggedId}`,
        { parent_id: null },
        { withCredentials: true }
      );
      toast.success(tr("Déplacé à la racine", "Moved to the root"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Déplacement impossible", "Move failed"));
    }
  };

  const renderNode = (node, depth = 0) => {
    const isCat = node.is_category;
    const isExpanded = expanded.has(node.id);
    const isDragOver = canEdit && isCat && dragOverId === node.id;
    return (
      <div key={node.id} data-testid={`ext-resource-${node.id}`}>
        <div
          draggable={canEdit}
          onDragStart={(e) => canEdit && e.dataTransfer.setData("text/x-ext-resource-id", node.id)}
          onDragOver={(e) => {
            if (!canEdit || !isCat) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setDragOverId(node.id);
          }}
          onDragLeave={() => { if (dragOverId === node.id) setDragOverId(null); }}
          onDrop={(e) => {
            if (!canEdit || !isCat) return;
            e.preventDefault();
            e.stopPropagation();  // évite le drop-on-root parent
            const draggedId = e.dataTransfer.getData("text/x-ext-resource-id");
            onDropOnCategory(draggedId, node.id);
          }}
          className={`flex items-center gap-2 py-2 px-3 rounded-lg group hover:bg-secondary/50 ${isCat ? "font-medium" : ""} ${isDragOver ? "ring-2 ring-[#002FA7]/60 bg-[#002FA7]/5" : ""} ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
          style={{ marginLeft: depth * 20 }}
        >
          {isCat ? (
            <button onClick={() => toggleExpanded(node.id)} className="flex-shrink-0" data-testid={`ext-toggle-${node.id}`} aria-label={isExpanded ? tr("Réduire", "Collapse") : "Développer"}>
              {isExpanded ? <FolderOpen size={18} className="text-amber-500" weight="fill" /> : <Folder size={18} className="text-amber-500" weight="fill" />}
            </button>
          ) : (
            <LinkIcon size={16} className="text-[#002FA7] flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            {isCat ? (
              <button onClick={() => toggleExpanded(node.id)} className="text-sm text-left truncate w-full" data-testid={`ext-cat-label-${node.id}`}>{node.title}</button>
            ) : (
              <a
                href={node.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#002FA7] hover:underline flex items-center gap-1 truncate"
                data-testid={`ext-link-${node.id}`}
              >
                <span className="truncate">{node.title}</span>
                <ArrowSquareOut size={12} className="flex-shrink-0" />
              </a>
            )}
            {!isCat && node.description && (
              <p className="text-[10px] text-muted-foreground truncate">{node.description}</p>
            )}
            {!isCat && canEdit && node.index_status && (
              <p className="text-[10px] flex items-center gap-1">
                {node.index_status === "success" && <><CheckCircle size={10} className="text-teal-500" weight="fill" /><span className="text-teal-600">{node.index_chunks || 0} chunks indexés</span></>}
                {node.index_status === "pending" && <span className="text-amber-500">Indexation en cours…</span>}
                {node.index_status === "partial" && <><WarningCircle size={10} className="text-amber-500" weight="fill" /><span className="text-amber-600">Indexation partielle : {node.index_error}</span></>}
                {node.index_status === "error" && <><WarningCircle size={10} className="text-rose-500" weight="fill" /><span className="text-rose-600">Indexation échouée : {(node.index_error || "").slice(0, 60)}</span></>}
              </p>
            )}
          </div>
          {canEdit && (
            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 flex-shrink-0 transition-opacity">
              {isCat && (
                <>
                  <button onClick={() => setShowForm({ mode: "create-cat", parent_id: node.id })} title={tr("Sous-catégorie", "Sub-category")} data-testid={`ext-add-subcat-${node.id}`} className="p-1 rounded hover:bg-amber-100 text-amber-600"><Folder size={14} /></button>
                  <button onClick={() => setShowForm({ mode: "create-item", parent_id: node.id })} title={tr("Ajouter un lien", "Add a link")} data-testid={`ext-add-item-${node.id}`} className="p-1 rounded hover:bg-blue-100 text-[#002FA7]"><Plus size={14} /></button>
                </>
              )}
              {!isCat && (
                <>
                  <button onClick={() => setPreviewResourceId(node.id)} title={tr("Voir le contenu indexé par le chatbot", "See what the chatbot has indexed")} data-testid={`ext-preview-${node.id}`} className="p-1 rounded hover:bg-indigo-100 text-indigo-600"><Eye size={14} /></button>
                  <button onClick={() => reindex(node)} title={tr("Réindexer pour le chatbot", "Reindex for the chatbot")} data-testid={`ext-reindex-${node.id}`} className="p-1 rounded hover:bg-teal-100 text-teal-600"><ArrowClockwise size={14} /></button>
                </>
              )}
              <button onClick={() => setShowForm({ mode: "edit", resource: node })} title={tr("Modifier", "Edit")} data-testid={`ext-edit-${node.id}`} className="p-1 rounded hover:bg-secondary text-muted-foreground"><PencilSimple size={14} /></button>
              <button onClick={() => deleteResource(node)} title={tr("Supprimer", "Delete")} data-testid={`ext-delete-${node.id}`} className="p-1 rounded hover:bg-rose-100 text-rose-600"><Trash size={14} /></button>
            </div>
          )}
        </div>
        {isCat && isExpanded && node.children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="ext-resources-title">Ressources externes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Liens vers des ressources web utiles à ta formation. {canEdit && "Le chatbot indexe leur contenu pour répondre à tes questions (mais elles ne servent pas aux quiz)."}
          </p>
          {/* Vincent B592.d (Eric 2026-07-28 SPEC STRICK) — Badge cron RAG, founder/admin uniquement */}
          {isFounderOrAdmin && <ReindexBadge status={reindexStatus} />}
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap items-start">
            <div className="flex flex-col gap-1">
              <Button onClick={reindexAll} variant="outline" disabled={reindexAllBusy} data-testid="ext-reindex-all-btn" title={tr("Rafraîchir le contenu de toutes les pages pour le Chatbot. Un cron automatique le fait chaque dimanche 03h00 (Montréal).", "Refreshes the content of every page for the chatbot. An automatic job already does it every Sunday at 03:00 (Montreal).")}>
                <ArrowClockwise size={16} className={`mr-1 ${reindexAllBusy ? "animate-spin" : ""}`} /> {reindexAllBusy ? "Lancement…" : "Réindexer maintenant"}
              </Button>
              {/* Vincent B598.c (Eric 2026-07-29) — Hint 1× par session */}
              {showReindexHint && (
                <p
                  className="text-[10px] text-muted-foreground/80 leading-tight max-w-[180px] animate-pulse cursor-pointer"
                  onClick={dismissReindexHint}
                  data-testid="ext-reindex-hint"
                >
                  {tr("Astuce : un cron auto le fait déjà chaque dimanche 03 h.", "Tip: an automatic job already does it every Sunday at 03:00.")}
                </p>
              )}
            </div>
            <Button onClick={() => setShowForm({ mode: "create-cat" })} variant="outline" data-testid="ext-add-cat-root-btn"><Folder size={16} className="mr-1" /> {tr("Catégorie", "Category")}</Button>
            <Button onClick={() => setShowForm({ mode: "create-item" })} className="bg-[#002FA7] hover:bg-[#002280]" data-testid="ext-add-item-root-btn"><Plus size={16} className="mr-1" /> Lien</Button>
          </div>
        )}
      </div>

      {loading && <p className="text-sm text-muted-foreground">Chargement…</p>}
      {!loading && tree.length === 0 && (
        <div className="text-center py-16 border border-dashed rounded-2xl">
          <LinkIcon size={48} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">{tr("Aucune ressource externe pour l\u2019instant.", "No external resource yet.")}</p>
          {canEdit && <p className="text-xs text-muted-foreground mt-2">{tr("Cliquez sur « Lien » ou « Catégorie » ci-dessus pour commencer.", "Click “Link” or “Category” above to get started.")}</p>}
        </div>
      )}
      {!loading && tree.length > 0 && (
        <div
          className={`bg-card border border-border rounded-2xl p-2 ${canEdit && dragOverId === "__root__" ? "ring-2 ring-[#002FA7]/60" : ""}`}
          data-testid="ext-resources-tree"
          onDragOver={(e) => {
            if (!canEdit) return;
            e.preventDefault();
            setDragOverId("__root__");
          }}
          onDragLeave={() => { if (dragOverId === "__root__") setDragOverId(null); }}
          onDrop={(e) => {
            if (!canEdit) return;
            e.preventDefault();
            const draggedId = e.dataTransfer.getData("text/x-ext-resource-id");
            onDropOnRoot(draggedId);
          }}
        >
          {tree.map((n) => renderNode(n))}
          {canEdit && <p className="text-[10px] text-center text-muted-foreground py-2 opacity-70">{tr("Astuce : glisser-déposer un élément sur une catégorie pour le déplacer.", "Tip: drag an item onto a category to move it.")}</p>}
        </div>
      )}

      {showForm && <ResourceForm form={showForm} resources={resources} onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); load(); }} />}
      {previewResourceId && <ChunksPreviewModal resourceId={previewResourceId} onClose={() => setPreviewResourceId(null)} />}
    </div>
  );
}

function ResourceForm({ form, resources, onClose, onSaved }) {
  const { tr } = useLang();
  const isEdit = form.mode === "edit";
  const isCat = form.mode === "create-cat" || (isEdit && form.resource?.is_category);
  const [title, setTitle] = useState(form.resource?.title || "");
  const [url, setUrl] = useState(form.resource?.url || "");
  const [parentId, setParentId] = useState(form.resource?.parent_id ?? form.parent_id ?? "");
  const [description, setDescription] = useState(form.resource?.description || "");
  const [busy, setBusy] = useState(false);

  const categories = resources.filter((r) => r.is_category && (!isEdit || r.id !== form.resource?.id));

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error(tr("Le titre est requis", "The title is required")); return; }
    if (!isCat && !url.trim()) { toast.error(tr("L'URL est requise pour un lien", "A link needs a URL")); return; }
    if (!isCat && !/^https?:\/\//.test(url.trim())) { toast.error("L'URL doit commencer par http:// ou https://"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/api/external-resources/${form.resource.id}`, {
          title: title.trim(),
          url: isCat ? undefined : url.trim(),
          parent_id: parentId || null,
          description: description.trim() || null,
        }, { withCredentials: true });
        toast.success(tr("Ressource modifiée", "Resource updated"));
      } else {
        await axios.post(`${API}/api/external-resources`, {
          title: title.trim(),
          url: isCat ? null : url.trim(),
          is_category: isCat,
          parent_id: parentId || null,
          description: description.trim() || null,
        }, { withCredentials: true });
        toast.success(isCat ? tr("Catégorie créée", "Category created") : "Lien ajouté — indexation en arrière-plan");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form className="bg-background rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="ext-resource-form">
        <h3 className="text-lg font-semibold">
          {isEdit ? tr("Modifier", "Edit") : (isCat ? "Nouvelle catégorie" : "Nouveau lien")}
        </h3>
        <div>
          <label className="text-xs text-muted-foreground">Titre</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="ext-form-title" />
        </div>
        {!isCat && (
          <div>
            <label className="text-xs text-muted-foreground">URL</label>
            <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" required data-testid="ext-form-url" />
          </div>
        )}
        <div>
          <label className="text-xs text-muted-foreground">{tr("Catégorie parente (optionnel)", "Parent category (optional)")}</label>
          <select value={parentId || ""} onChange={(e) => setParentId(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="ext-form-parent">
            <option value="">{tr("Racine (aucune catégorie)", "Root (no category)")}</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>
        {!isCat && (
          <div>
            <label className="text-xs text-muted-foreground">Description (optionnel)</label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="ext-form-desc" />
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1" data-testid="ext-form-cancel">{tr("Annuler", "Cancel")}</Button>
          <Button type="submit" disabled={busy} className="flex-1 bg-[#002FA7] hover:bg-[#002280]" data-testid="ext-form-save">{busy ? "…" : "Enregistrer"}</Button>
        </div>
      </form>
    </div>
  );
}



// Vincent B592.b (Eric 2026-07-28) — Modal prévisualisation des chunks
// indexés par le chatbot pour une ressource. Utile pour déboguer un
// résultat RAG imprécis.
function ChunksPreviewModal({ resourceId, onClose }) {
  const { tr } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/external-resources/${resourceId}/chunks`, { withCredentials: true });
        if (!cancelled) setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || tr("Impossible de charger les chunks", "Could not load the chunks"));
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [resourceId, onClose]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ext-preview-modal">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-base">{tr("Contenu indexé par le chatbot", "Content indexed by the chatbot")}</h3>
          {data && (
            <p className="text-[11px] text-muted-foreground truncate">
              {data.title} · {data.total_chunks} chunk(s) · {data.total_chars.toLocaleString("fr")} caractères
              {data.indexed_at && <span> · indexé {new Date(data.indexed_at).toLocaleString("fr")}</span>}
            </p>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && <p className="text-sm text-muted-foreground">Chargement…</p>}
          {!loading && data && data.chunks.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">{tr("Aucun chunk indexé.", "No chunk indexed.")}</p>
              {data.index_status === "error" && <p className="text-xs text-rose-600 mt-2">Erreur : {data.index_error}</p>}
              {data.index_status === "pending" && <p className="text-xs text-amber-600 mt-2">Indexation en cours — réessayer dans quelques secondes.</p>}
            </div>
          )}
          {!loading && data && data.chunks.map((c) => (
            <div key={c.id} className="bg-muted/40 border border-border rounded-lg p-3" data-testid={`ext-preview-chunk-${c.chunk_index}`}>
              <p className="text-[10px] text-muted-foreground mb-1">Chunk #{c.chunk_index + 1} · {c.content.length} caractères</p>
              <p className="text-xs whitespace-pre-wrap leading-relaxed">{c.content}</p>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-border">
          <button onClick={onClose} className="w-full py-2 px-4 rounded-md border border-border hover:bg-secondary text-sm" data-testid="ext-preview-close">{tr("Fermer", "Close")}</button>
        </div>
      </div>
    </div>
  );
}


// Vincent B592.d (Eric 2026-07-28 SPEC STRICK) — Badge de santé du cron
// hebdomadaire + dernière réindexation manuelle. Visible UNIQUEMENT
// founder/admin (gate côté parent). Backend garantit aussi 403 sinon.
function ReindexBadge({ status }) {
  const { tr } = useLang();
  if (!status) return null;
  const manual = status.manual;
  const weekly = status.weekly_cron;
  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffH = Math.floor(diffMs / 3_600_000);
    if (diffH < 1) return "il y a moins d'une heure";
    if (diffH < 24) return `il y a ${diffH} h`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `il y a ${diffD} j`;
    return d.toLocaleDateString("fr");
  };
  // Priorité au dernier run (manuel OU cron) le plus récent
  const manualAt = manual?.finished_at || manual?.started_at || null;
  const weeklyAt = weekly?.last_run?.finished_at || weekly?.last_run?.started_at || weekly?.started_at || null;
  const last = manualAt && (!weeklyAt || manualAt > weeklyAt) ? manual : weekly?.last_run;
  const lastAt = manualAt && (!weeklyAt || manualAt > weeklyAt) ? manualAt : weeklyAt;
  const source = manualAt && (!weeklyAt || manualAt > weeklyAt) ? "Manuel" : "Cron auto";
  const success = last?.summary?.success ?? last?.success;
  const total = last?.summary?.total ?? last?.total;
  const errors = last?.summary?.error ?? last?.error;
  return (
    <div className="mt-2 inline-flex items-center gap-2 text-[11px] px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-800" data-testid="ext-reindex-badge">
      <ArrowClockwise size={12} weight="bold" />
      <span>
        Dernière réindexation ({source}) : <strong>{fmt(lastAt)}</strong>
        {total !== undefined && ` · ${success ?? 0}/${total} OK`}
        {errors ? ` · ${errors} erreur(s)` : ""}
      </span>
      <span className="text-[10px] text-blue-600/70 ml-1">{tr("Cron auto : dimanche 03 h (Montréal)", "Automatic job: Sunday 03:00 (Montreal)")}</span>
    </div>
  );
}
